/* eslint-disable react/prop-types */
import { DrawerActions } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import ReAnimated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { useSafeAreaFrame } from 'react-native-safe-area-context';
import { mvs } from 'react-native-size-matters';
import { State, usePlaybackState } from 'react-native-track-player';

import AllPodcastsModalScreen from './AllPodcastsModalScreen';
import { useAppSelector } from '../../app/hooks';
import CustomText from '../../components/CustomText';
import { MenuIcon } from '../../components/MenuIcon';
import {
  PodcastHeroCard,
  PodcastTitleCard,
  ResumeEpisodeCard,
} from '../../components/Podcast';
import { getPodcastHeroCardItemLayout } from '../../components/Podcast/PodcastHeroCard';
import { getPodcastTitleCardItemLayout } from '../../components/Podcast/PodcastTitleCard';
import { ScreenLayout } from '../../components/ScreenLayout';
import { TabName } from '../../components/TabName';
import {
  COLORS,
  DataState,
  IS_WEB,
  isTablet,
  PODCAST_HERO_CARD_HEIGHT,
  PODCAST_TITLE_CARD_HEIGHT,
} from '../../constants';
import { selectAppConfig } from '../../features/appconfig/store/selectors';
import { selectCurrentEpisode } from '../../features/currentpodcast/store/selectors';
import { selectRadioMode } from '../../features/currentradio/store/selectors';
import { selectResumeEpisode } from '../../features/podcastList/store/selectors';
import { getResponsiveCardWidth } from '../../helpers/getResponsiveCardWidth';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { useDisableDrawerSwipeOnFocus } from '../../hooks/useDisableDrawerSwipeOnFocus';
import useOrientation from '../../hooks/useOrientation';
import { usePodcastListAndEpisodes } from '../../hooks/usePodcastListAndEpisodes';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import i18n from '../../localization/i18n';
import { PodcastEpisodeModel, PodcastModel, PodcastTabModel } from '../../models';
import { PodcastStackNavigatorParams } from '../../navigation/PodcastStackNavigator';

type PodcastHomeScreenProps = NativeStackScreenProps<
  PodcastStackNavigatorParams,
  'PodcastHome'
>;

export default function PodcastHomeScreen({
  route,
  navigation,
}: PodcastHomeScreenProps): React.JSX.Element {
  // extract tab data from route params
  const tabOptions = useMemo(() => {
    return route.params.options as PodcastTabModel;
  }, [route.params]);

  // get app configuration from redux store
  const appConfig = useAppSelector(selectAppConfig);
  // get tab index
  const tabIndex = useMemo(() => {
    return appConfig.onglets.findIndex(tab => tab.idonglets === tabOptions.idonglets);
  }, [appConfig.onglets, tabOptions.idonglets]);
  const podcastTabConfig = appConfig.onglets[tabIndex] as PodcastTabModel;
  // get podcast feeds list for fetching screen data
  const podcastFeedList = podcastTabConfig?.podcastsRssList;

  // fetch podcast data
  const { podcastList, podcastListState, latestEpisodesList, latestEpisodesListState } =
    usePodcastListAndEpisodes(tabOptions.idonglets, podcastFeedList);

  // custom design hooks
  const { bodyHorizontalPadding } = useResponsiveLayout();
  const orientation = useOrientation();
  const titleFontSize = useAdjustedFontSize(mvs(25));
  const titleLineHeight = useAdjustedFontSize(mvs(30));
  const sectionTitleFontSize = useAdjustedFontSize(mvs(20));
  const sectionTitleLineHeight = useAdjustedFontSize(mvs(24));
  const { width } = useSafeAreaFrame();

  const radioMode = useAppSelector(selectRadioMode);

  // most recently interrupted episode to offer for resuming (APPS-2160)
  const resumeEpisode = useAppSelector(selectResumeEpisode);
  const currentEpisode = useAppSelector(selectCurrentEpisode);
  const playbackState = usePlaybackState().state;
  // when the resume episode is the one loaded in the player (mini player),
  // the section is redundant whether playing or paused — always hide it.
  const isResumeEpisodeInPlayer =
    !radioMode && !!resumeEpisode && resumeEpisode.episode.id === currentEpisode.id;
  // hidden by default: only show once the player reports a definite idle state.
  // On mount (tab switch) the state is briefly undefined and episode launches
  // pass through ready/buffering/loading, so requiring an explicit idle state
  // keeps the section from flashing in those windows. Radio playback keeps it
  // visible.
  const isPlayerIdle =
    playbackState === State.None ||
    playbackState === State.Paused ||
    playbackState === State.Stopped ||
    playbackState === State.Ended ||
    playbackState === State.Error;
  const resumeSectionEligible =
    !!resumeEpisode && !isResumeEpisodeInPlayer && (radioMode || isPlayerIdle);

  const [showResumeSection, setShowResumeSection] = useState(false);
  useEffect(() => {
    if (!resumeSectionEligible) {
      setShowResumeSection(false);
      return;
    }
    const timer = setTimeout(() => setShowResumeSection(true), 500);
    return () => clearTimeout(timer);
  }, [resumeSectionEligible]);

  // for tablet layout, calculate number of columns based on device and orientation
  const numColumns = useMemo(() => {
    if (!isTablet) {
      return 1; // mobile always uses 1 column (horizontal list)
    }
    return orientation === 'LANDSCAPE' ? 4 : 3;
  }, [orientation]);

  // disable drawer gestures when focused to prevent conflicts with horizontal scrolling
  useDisableDrawerSwipeOnFocus(navigation);

  // modal state for full podcast list
  const [isAllPodcastsModalVisible, setIsAllPodcastsModalVisible] = useState(false);

  // navigation handlers
  const toggleLeftDrawer = useCallback(() => {
    navigation.dispatch(DrawerActions.toggleDrawer());
  }, [navigation]);
  const handlePodcastPress = useCallback(
    (podcastUrl: string) => {
      navigation.navigate('PodcastFeed', { podcastUrl });
    },
    [navigation],
  );
  const handleSeeAllPress = useCallback(() => {
    setIsAllPodcastsModalVisible(true);
  }, []);
  const handleCloseAllPodcastsModal = useCallback(() => {
    setIsAllPodcastsModalVisible(false);
  }, []);
  const handlePodcastPressFromModal = useCallback(
    (podcastUrl: string) => {
      setIsAllPodcastsModalVisible(false);
      handlePodcastPress(podcastUrl);
    },
    [handlePodcastPress],
  );
  const handleEpisodePress = useCallback(
    (episode: PodcastEpisodeModel) => {
      navigation.navigate('PodcastEpisode', {
        episode,
      });
    },
    [navigation],
  );

  const renderEpisode = useCallback(
    ({ item }: { item: PodcastEpisodeModel; index: number }) => {
      return <PodcastHeroCard episode={item} onPress={() => handleEpisodePress(item)} />;
    },
    [handleEpisodePress],
  );

  const renderPodcast = useCallback(
    ({ item }: { item: Partial<PodcastModel> }) => {
      const cardWidth = isTablet
        ? getResponsiveCardWidth({
            width,
            numColumns,
            horizontalPadding: bodyHorizontalPadding,
          })
        : undefined;
      return (
        <PodcastTitleCard
          podcastThumbnail={item.logo}
          podcastName={item.title || ''}
          onPress={() => handlePodcastPress(item.url || '')}
          customWidth={cardWidth}
        />
      );
    },
    [width, numColumns, bodyHorizontalPadding, handlePodcastPress],
  );

  const visibleResumeEpisode = showResumeSection ? resumeEpisode : undefined;

  // performance hack: episodes are rendered as list header to avoid nested scrollviews
  const listHeader = useMemo(() => {
    return (
      <View style={styles.section}>
        {visibleResumeEpisode && (
          <ReAnimated.View
            style={styles.resumeSection}
            entering={FadeIn.duration(250)}
            exiting={FadeOut.duration(250)}
          >
            <CustomText
              style={[
                styles.sectionTitle,
                {
                  fontSize: sectionTitleFontSize,
                  lineHeight: sectionTitleLineHeight,
                  paddingLeft: bodyHorizontalPadding,
                },
              ]}
              weight="Bold"
            >
              {i18n.t('podcast.resume')}
            </CustomText>
            <View
              style={[
                styles.resumeCardWrapper,
                { paddingHorizontal: bodyHorizontalPadding },
              ]}
            >
              <ResumeEpisodeCard
                episode={visibleResumeEpisode.episode}
                position={visibleResumeEpisode.position}
                duration={visibleResumeEpisode.duration}
                onPress={() => handleEpisodePress(visibleResumeEpisode.episode)}
              />
            </View>
          </ReAnimated.View>
        )}

        <CustomText
          style={[
            styles.sectionTitle,
            {
              fontSize: sectionTitleFontSize,
              lineHeight: sectionTitleLineHeight,
              paddingLeft: bodyHorizontalPadding,
            },
          ]}
          weight="Bold"
        >
          {i18n.t('podcast.latestEpisodes')}
        </CustomText>

        {latestEpisodesListState === DataState.LOADING ? (
          <View style={styles.heroCardLoaderContainer}>
            <ActivityIndicator size="large" color={COLORS.WHITE} />
          </View>
        ) : (
          <FlatList
            data={latestEpisodesList || []}
            renderItem={renderEpisode}
            keyExtractor={item => item.id}
            getItemLayout={getPodcastHeroCardItemLayout}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={[
              styles.horizontalList,
              { paddingLeft: bodyHorizontalPadding },
            ]}
            style={styles.latestList}
            maxToRenderPerBatch={2}
            initialNumToRender={2}
            windowSize={3}
            removeClippedSubviews={true}
          />
        )}

        {/* all podcasts section header */}
        <View style={[styles.sectionHeader, styles.podcastSectionHeader]}>
          <CustomText
            style={[
              styles.sectionTitle,
              {
                fontSize: sectionTitleFontSize,
                lineHeight: sectionTitleLineHeight,
                paddingLeft: bodyHorizontalPadding,
              },
            ]}
            weight="Bold"
          >
            {i18n.t('podcast.all')}
          </CustomText>
          {!isTablet && (
            <TouchableOpacity onPress={handleSeeAllPress}>
              <CustomText
                style={[styles.seeAllText, { marginRight: bodyHorizontalPadding }]}
                weight="SemiBold"
              >
                {i18n.t('podcast.seeAll')}
              </CustomText>
            </TouchableOpacity>
          )}
        </View>

        {/* loading state for podcasts */}
        {podcastListState === DataState.LOADING && (
          <View style={styles.titleCardLoaderContainer}>
            <ActivityIndicator size="large" color={COLORS.WHITE} />
          </View>
        )}
      </View>
    );
  }, [
    sectionTitleFontSize,
    sectionTitleLineHeight,
    latestEpisodesListState,
    latestEpisodesList,
    renderEpisode,
    handleSeeAllPress,
    bodyHorizontalPadding,
    podcastListState,
    visibleResumeEpisode,
    handleEpisodePress,
  ]);

  return (
    <ScreenLayout
      isPodcastModule
      headerContents={
        // header with menu button and tab title
        <View style={styles.header}>
          <MenuIcon
            color={podcastTabConfig?.headerTextColor}
            toggleLeftDrawer={toggleLeftDrawer}
          />
          <TabName
            title={podcastTabConfig?.titre}
            titleFontSize={titleFontSize}
            color={podcastTabConfig?.headerTextColor}
            titleLineHeight={titleLineHeight}
          />
        </View>
      }
    >
      {/* main content with single flatlist */}
      {podcastListState === DataState.LOADING ? (
        // show episodes header with loading state for podcasts
        <View style={[styles.container]}>
          {/* loading state header */}
          {listHeader}
        </View>
      ) : isTablet ? (
        // tablet: use vertical grid for podcasts with episodes header
        <FlatList
          data={podcastList || []}
          renderItem={renderPodcast}
          keyExtractor={item => item.id || ''}
          numColumns={numColumns}
          key={`tablet-${numColumns}-${orientation}`}
          ListHeaderComponent={listHeader}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.tabletContentContainer}
          style={[styles.container]}
          columnWrapperStyle={
            numColumns > 1
              ? [styles.tabletRow, { paddingHorizontal: bodyHorizontalPadding }]
              : undefined
          }
          maxToRenderPerBatch={4}
          initialNumToRender={6}
          windowSize={5}
          removeClippedSubviews={true}
        />
      ) : (
        // mobile: episodes as header with horizontal podcast list
        <ScrollView
          style={[styles.container]}
          contentContainerStyle={styles.scrollContent}
        >
          {listHeader}
          <FlatList
            data={podcastList || []}
            renderItem={renderPodcast}
            keyExtractor={item => item.id || ''}
            getItemLayout={getPodcastTitleCardItemLayout}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={[
              styles.horizontalList,
              { paddingLeft: bodyHorizontalPadding },
            ]}
            maxToRenderPerBatch={2}
            initialNumToRender={2}
            windowSize={3}
            removeClippedSubviews={true}
          />
        </ScrollView>
      )}

      {/* full screen podcast grid modal on phone only */}
      <AllPodcastsModalScreen
        visible={isAllPodcastsModalVisible}
        onRequestClose={handleCloseAllPodcastsModal}
        podcastList={podcastList}
        podcastListState={podcastListState}
        onPodcastPress={handlePodcastPressFromModal}
      />
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    paddingBottom: IS_WEB ? mvs(24) : undefined,
  },
  section: {
    marginBottom: mvs(16),
  },
  resumeSection: {
    marginBottom: mvs(24),
  },
  resumeCardWrapper: {
    marginTop: mvs(16),
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  podcastSectionHeader: {
    marginTop: isTablet ? mvs(42) : mvs(32),
  },
  sectionTitle: {
    color: COLORS.WHITE,
  },
  seeAllText: {
    fontSize: mvs(14),
    opacity: 0.7,
    color: COLORS.WHITE,
  },
  horizontalList: {
    gap: mvs(16),
    paddingRight: mvs(16),
  },
  latestList: {
    marginTop: mvs(16),
  },
  scrollContent: {
    paddingBottom: mvs(36),
  },
  tabletContentContainer: {
    paddingBottom: mvs(32),
  },
  tabletRow: {
    justifyContent: 'flex-start',
    gap: mvs(12),
    paddingHorizontal: 0,
    marginBottom: mvs(12),
  },
  heroCardLoaderContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: mvs(PODCAST_HERO_CARD_HEIGHT),
    width: '100%',
    marginTop: mvs(16),
  },
  titleCardLoaderContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: mvs(PODCAST_TITLE_CARD_HEIGHT),
    width: '100%',
  },
});
